"use client";

import { Intervals } from "@/constants";
import { VoidFn } from "@/types";
import { FormatText } from "@/utils/format-text";
import { wait } from "@/utils/wait";
import { useSession } from "next-auth/react";
import * as React from "react";
import { createStore, useStore } from "zustand";
import { persist } from "zustand/middleware";

export const OnboardingPlaces = {
  userProfile: "1-user-profile",
  userSidebar: "2-user-sidebar",
} as const;

export type OnboardingStep =
  `onboarding-${(typeof OnboardingPlaces)[keyof typeof OnboardingPlaces]}-step-${number}`;

export const onboardingConfig = {
  intro:
    FormatText.stripWhitespace(`Welcome to Denomos—where the best crypto minds meet! 
    Discover expert strategies, compete in tournaments, and grow your portfolio alongside top investors.`),
  followUser: FormatText.stripWhitespace(
    `Follow this investor to get real-time updates on their moves and exclusive insights available only to followers.`,
  ),
  followLeaderboard:
    FormatText.stripWhitespace(`Follow the top-performing investors and get notified whenever they update their portfolio. 
    Learn from the best to sharpen your own strategies!`),
  portfolioCreate: FormatText.stripWhitespace(
    `Create your own portfolio and showcase your strategy! Plus, you could win a $300 reward for participating.”`,
  ),
  postCreate: FormatText.stripWhitespace(
    `Craft your posts with ease using our Notion-like editor, and share your thoughts and strategies with the community.`,
  ),
} as const;

interface OnboardingStore {
  // readonly isDone: boolean;
  readonly allSteps: OnboardingStep[];
  readonly onboardingSteps: OnboardingStep[];
  readonly currentStep: OnboardingStep | undefined;
  // readonly totalSteps: number;
  readonly readSteps: Record<string, boolean>;
  readonly prev: VoidFn | undefined;
  readonly next: VoidFn | undefined;
}

const onboardingStore = createStore(
  persist<OnboardingStore>(
    (set, get) => ({
      // isDone: false,
      currentStep: undefined,
      allSteps: [],
      onboardingSteps: [],
      readSteps: {},
      prev: () => {
        const allSteps = get().allSteps;
        const onboardingSteps = [...get().onboardingSteps];
        let currentStep = get().currentStep;
        if (currentStep) {
          const previousStepIndex = allSteps.indexOf(currentStep) - 1;
          currentStep = allSteps[previousStepIndex];
          if (currentStep) {
            onboardingSteps.unshift(currentStep);
          }
        }

        set({
          currentStep,
          onboardingSteps,
          readSteps: {
            ...get().readSteps,
            ...(currentStep ? { [currentStep]: false } : {}),
          },
        });
      },
      next: () => {
        const onboardingSteps = [...get().onboardingSteps];
        const currentStep = onboardingSteps.shift();
        set({
          onboardingSteps,
          currentStep: onboardingSteps[0],
          readSteps: {
            ...get().readSteps,
            ...(currentStep ? { [currentStep]: true } : {}),
          },
        });
      },
    }),
    {
      name: "pw-onboarding-store",
      migrate: (state) => {
        return {
          //@ts-ignore
          ...state,
          currentStep: undefined,
          allSteps: [],
          readSteps: {},
          onboardingSteps: [],
        };
      },
      version: 1.3,
    },
  ),
);

const OnboardingContext = React.createContext<typeof onboardingStore | null>(
  null,
);

export function OnboardingProvider({ children }: React.PropsWithChildren) {
  const { data: session } = useSession();
  React.useEffect(() => {
    if (!session) {
      return;
    }
    let onboardingItems =
      document.querySelectorAll("[data-onboarding-step]") ?? [];
    const getAbortControllers = (length: number) => {
      return Array.from({ length }, () => new AbortController());
    };
    let abortControllers = getAbortControllers(onboardingItems.length);

    const loadOnboardingItems = async () => {
      await wait(Intervals.SECOND * 5);

      onboardingItems = document.querySelectorAll("[data-onboarding-step]");

      abortControllers = getAbortControllers(onboardingItems.length);
      const steps: OnboardingStep[] = [];

      onboardingItems.forEach((item, index) => {
        const step = item.getAttribute(
          "data-onboarding-step",
        ) as OnboardingStep;
        steps.push(step);
        const { signal } = abortControllers[index];

        item.addEventListener(
          "click",
          () => {
            onboardingStore.setState({
              currentStep: step,
            });
          },
          { signal },
        );
      });

      const onboardingState = onboardingStore.getState();

      // Make sure that we don't have duplicates
      const allSteps = Array.from(
        new Set([...onboardingState.allSteps, ...steps]),
      );
      const onboardingSteps = Array.from(
        new Set([...onboardingState.onboardingSteps, ...steps]),
      ).filter((step) => !onboardingState.readSteps[step]);

      onboardingStore.setState({
        onboardingSteps,
        allSteps,
        currentStep: onboardingSteps[0],
      });
    };

    loadOnboardingItems();

    return () => {
      abortControllers.forEach((controller) => {
        controller.abort();
      });
    };
  }, [session]);

  return (
    <OnboardingContext.Provider value={onboardingStore}>
      {children}
    </OnboardingContext.Provider>
  );
}

function useOnboardingContext() {
  const context = React.useContext(OnboardingContext);
  if (!context) {
    throw new Error(
      "useOnboardingContext must be used within a OnboardingProvider",
    );
  }
  return context;
}

export function useOnboardingStore<T extends unknown>(
  selector: (state: OnboardingStore) => T,
) {
  const store = useOnboardingContext();
  return useStore<typeof store, T>(store, selector);
}
